import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { Close, InfoOutlined, Person } from '@mui/icons-material';
import { selectIncentivesAccess } from '../../../store/salesIncentivesSlice';
import { untaggedIncentiveLineCount } from '../../../utils/incentives';

// Non-blocking reminder at checkout: incentive items with no salesperson earn nobody. Never affects payment.
// "Tag salesperson" hands back to the billing screen (onTag) so checkout's own Enter/Esc keys stay untouched.
export default function UntaggedIncentiveStrip({ onTag }) {
  const access = useSelector(selectIncentivesAccess);
  const productIds = useSelector((s) => s.salesIncentives.productIds);
  const { currentOrderId, cartItems, selectedSalesperson } = useSelector((s) => s.order);
  const [dismissedFor, setDismissedFor] = useState(null);

  const count = useMemo(() => (access ? untaggedIncentiveLineCount(cartItems, new Set(productIds), selectedSalesperson) : 0),
    [access, cartItems, productIds, selectedSalesperson]);

  if (!count || dismissedFor === currentOrderId) return null;
  const text = `${count} incentive item${count === 1 ? ' has' : 's have'} no salesperson. Tag one so the sale is credited.`;

  return (
    <Box role="status" sx={{
      bgcolor: '#FFFBEB', color: '#92400E', borderBottom: '1px solid #E2E8F0', flexShrink: 0,
      px: { xs: 1.5, sm: 2.5 }, py: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: { xs: 'wrap', sm: 'nowrap' },
    }}>
      <InfoOutlined sx={{ fontSize: 18, flexShrink: 0 }} />
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, flex: 1, minWidth: 0 }}>{text}</Typography>
      {onTag && (
        <Button size="small" startIcon={<Person sx={{ fontSize: '16px !important' }} />} onClick={onTag}
          sx={{ order: { xs: 3, sm: 0 }, width: { xs: '100%', sm: 'auto' }, minHeight: { xs: 40, sm: 32 }, textTransform: 'none', fontWeight: 700, fontSize: '0.75rem',
            color: '#92400E', border: '1px solid #FCD34D', borderRadius: '8px', bgcolor: '#FFF', '&:hover': { bgcolor: '#FEF3C7' } }}>
          Tag salesperson
        </Button>
      )}
      <IconButton size="small" aria-label="Dismiss" onClick={() => setDismissedFor(currentOrderId)} sx={{ color: '#92400E', width: 32, height: 32 }}>
        <Close sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
}
